import { MarketRowScrollable } from "./components/MarketRowScrollable";
import { MarketRow } from "./components/MarketRow";
import React, { useCallback, useRef, useMemo, useEffect } from "react";
import { Grid } from "components/src/components/Grid/Grid";
import { MarketProps } from "./types";
import { MarketButton } from "../../../MarketButton/MarketButton";
import { MappedSelection } from "./components/MappedSelection";
import { Label } from "./components/Label";
import { randomIdString } from "shared/src/utils/randomIdString";
import { createMetaData } from "./js/createMetaData";
import { useSize } from "shared/src/hooks/useSize";
import { checkHasContainerQueries } from "shared/src/utils/supports";
import { useCompetitionType } from "shared/src/hooks/useCompetitionType";
import fastdom from "fastdom";

const defaultHandler = () => {};

const hasContainerQueries = checkHasContainerQueries();

export const Market = ({
  selections,
  competitor,
  competitors,
  type = "row",
  name,
  marketHeader,
  buttonOnly = false,
  displayOrder,
  groupBy,
  oddsFormat = "american",
  dataFilterGroup,
  canSelectMarkets = true,
  selectedSelectionIds,
  marketTemplateTitle,
  marketTemplateType,
  onSelectionClick = defaultHandler,
  updateMarket = defaultHandler,
  useSingleSelection = false,
  showVersusDivider = true,
  id: marketId,
  sport: sportProp,
  league: leagueProp,
  competition: competitionProp,
  competitionType,
  hasScore,
  isOddsBoost,
  mode,
  hideCompetitor,
  preventScroll = false,
  scrollType = "row",
  initialScrolledSelectionIndex,
  interactedTemplateState,
  containerSize,
  forceScrollableNav,
  scrollableAutoCenterSelected,
  canShowVersusDivider,
  isSelectedBuilder,
  priceDisplayFormatter,
  ...rest
}: MarketProps) => {
  const { sport, league } = useCompetitionType({ sport: sportProp, league: leagueProp, competition: competitionProp, competitionType });
  const randIdString = useRef(`cui__market-${randomIdString()}`);
  const wrapperRef = useRef<HTMLDivElement>(null);
  const hasCompetitor = !!competitor?.displayName || !!competitor?.team;
  const hasName = !!name;
  const isScrollable = type === "scrollable" ||
    (selections.length > 3 && !preventScroll && scrollType === "row");
  const hasMultipleCompetitors =
    Array.isArray(competitors) && competitors.length === 2;

  const handleSingleSelectionClick = useCallback(
    (e, id) => {
      if (
        canSelectMarkets ||
        (selectedSelectionIds &&
          selectedSelectionIds.includes(selections[0].id))
      ) {
        onSelectionClick({ e, id, selection: selections[0], updateMarket });
      }
    },
    [selections[0], updateMarket, canSelectMarkets]
  );

  const marketButtonData = {
    type,
    scrollType,
    isScrollable,
    onSelectionClick,
    oddsFormat,
    updateMarket,
    buttonOnly,
    sport,
    name,
    canSelectMarkets,
    selectedSelectionIds,
    dataFilterGroup,
    marketHeader,
    marketTemplateTitle,
    competitor,
    competitors,
    isOddsBoost,
    isSelectedBuilder,
    priceDisplayFormatter,
  };

  const { width } = useSize(wrapperRef.current, 200, sport !== "tennis") || {
    width: null,
  };

  const marketCompetitorProps = useMemo(
    () => ({
      hasMultipleCompetitors,
      competitor,
      competitors,
      buttonOnly,
      type,
      league,
      sport,
      competitionType,
      mode,
      showVersusDivider,
      canShowVersusDivider,
      hasScore,
      name,
      isOddsBoost,
    }),
    [
      hasMultipleCompetitors,
      competitor,
      competitors,
      buttonOnly,
      type,
      league,
      sport,
      competitionType,
      mode,
      showVersusDivider,
      hasScore,
      name,
      isOddsBoost
    ]
  );

  useEffect(() => {
    if (!hasContainerQueries) {
      fastdom.mutate(() => {
        if (
          wrapperRef.current &&
          wrapperRef.current.offsetParent !== null &&
          wrapperRef.current.offsetParent instanceof HTMLElement
        ) {
          wrapperRef.current.offsetParent.style.setProperty(
            "--cui-market-template-competitor-width",
            `${wrapperRef.current?.clientWidth || 120}px`
          );
        }
      });
    }
  }, [width]);

  const metadata = createMetaData({
    competitor,
    competitors,
    marketHeader,
    sport,
    name,
    i: 0,
    marketTemplateTitle,
  });

  if (type === "rowgrouped") {
    return selections?.map((selection, i) => (
      <MappedSelection
        key={`${selection?.id}-${i}`}
        index={i}
        selection={selection}
        marketButtonData={marketButtonData}
      />
    ));
  }

  if (type === "row" || type === "scrollable") {
    if (isScrollable) {
      return (
        <MarketRowScrollable
          randIdString={randIdString}
          useSingleSelection={useSingleSelection}
          dataFilterGroup={dataFilterGroup}
          marketId={marketId}
          rest={rest}
          wrapperRef={wrapperRef}
          marketCompetitorProps={marketCompetitorProps}
          selections={selections}
          marketButtonData={marketButtonData}
          interactedTemplateState={interactedTemplateState}
          initialScrolledSelectionIndex={initialScrolledSelectionIndex}
          containerSize={containerSize}
          forceScrollableNav={forceScrollableNav}
          scrollableAutoCenterSelected={scrollableAutoCenterSelected}
        />
      );
    } else {
      return (
        <MarketRow
          useSingleSelection={useSingleSelection}
          dataFilterGroup={dataFilterGroup}
          marketId={marketId}
          rest={rest}
          marketTemplateType={marketTemplateType}
          hasCompetitor={hasCompetitor}
          hasMultipleCompetitors={hasMultipleCompetitors}
          hasName={hasName}
          hideCompetitor={hideCompetitor}
          wrapperRef={wrapperRef}
          selections={selections}
          randIdString={randIdString}
          marketButtonData={marketButtonData}
          marketCompetitorProps={marketCompetitorProps}
          canShowVersusDivider={canShowVersusDivider}
          showVersusDivider={showVersusDivider}
          hasScore={hasScore}
          isOddsBoost={isOddsBoost}
          sport={sport}
          type={type}
        />
      );
    }
  }

  return (
    <div
      className="cui__market-col cui-flex cui-flex-col cui-gap-2xs cui-justify-end"
      data-filter-group-set={`cui__market-col-${marketId}`}
    >
      {hasCompetitor || hasName ? (
        <Grid.Cell className="cui-flex cui-items-center cui-justify-center">
          <Label
            competitor={competitor}
            name={name}
            buttonOnly={buttonOnly}
            type={type}
            hideScore={true}
            league={league}
            sport={sport}
            competitionType={competitionType}
          />
        </Grid.Cell>
      ) : null}
      {useSingleSelection ? (
        <MarketButton
          {...selections[0]}
          metadata={metadata}
          onClick={handleSingleSelectionClick}
          animation={selections[0]?.animation ?? "reduced"}
          fluid={true}
          isDisabled={
            selections[0]?.isDisabled ||
            (!canSelectMarkets &&
              selectedSelectionIds &&
              !selectedSelectionIds.includes(selections[0]?.id))
          }
        />
      ) : (
        selections?.map((selection, i) => (
          <MappedSelection
            key={`${selection?.id}-${i}`}
            index={i}
            selection={selection}
            marketButtonData={marketButtonData}
          />
        ))
      )}
    </div>
  );
};
